import React, { useState, useEffect } from "react";
import PlanManagementPage from "../../../entities/SuperAdminPage/PlanManagement";
import { planService } from "../../../api";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../context/AlertContext";
import { useLocation, useNavigate } from "react-router-dom";

const PlanManagement = () => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const selectedCountry = String(location?.state?.country || "").trim();
  const readOnly = location?.state?.readOnly === true;

  const normalizeCountry = (country) =>
    String(country || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");

  useEffect(() => {
    fetchPlans();
  }, [selectedCountry]);

  const normalizeListResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.result)) return response.result;
    return [];
  };

  const fetchPlans = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await planService.list(selectedCountry);
      const records = normalizeListResponse(result);
      const nextPlans = Array.isArray(records) ? records : [];
      const sortPlans = (list) => {
        return [...list].sort((a, b) => {
          const nameA = String(a?.planName || "").trim().toLowerCase();
          const nameB = String(b?.planName || "").trim().toLowerCase();

          // 1. Trial Plan always first
          if (nameA === "trial plan") return -1;
          if (nameB === "trial plan") return 1;

          // 2. Then sort by price ascending
          const priceA = Number(a?.price) || 0;
          const priceB = Number(b?.price) || 0;
          return priceA - priceB;
        });
      };

      if (!selectedCountry) {
        setPlans(sortPlans(nextPlans));
      } else {
        const targetCountry = normalizeCountry(selectedCountry);
        // New model: a plan serves a country if it has an ACTIVE pricing[] entry
        // for it. Fall back to the legacy single `plan.country` field.
        const filteredPlans = nextPlans.filter((plan) => {
          if (Array.isArray(plan?.pricing) && plan.pricing.length) {
            return plan.pricing.some(
              (p) => p?.isActive === true && normalizeCountry(p?.country) === targetCountry
            );
          }
          return normalizeCountry(plan?.country) === targetCountry;
        });
        setPlans(sortPlans(filteredPlans));
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error fetching plans", "error");
    }
  };

  const handleEditPlan = (planId) => {
    navigate(`/dashboard/planManagement/edit/${planId}`);
  };

  const handleTogglePlanStatus = async (planId, currentStatus) => {
    const nextStatus = !currentStatus;
    LoaderHelper.loaderStatus(true);
    try {
      const result = await planService.update(planId, { isActive: nextStatus });
      if (result?.success !== false) {
        showAlert(
          result?.message || `Plan ${nextStatus ? "activated" : "disabled"} successfully`,
          "success"
        );
        await fetchPlans();
      } else {
        showAlert(result?.message || `Failed to ${nextStatus ? "activate" : "disable"} plan`, "error");
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || `Error ${nextStatus ? "activating" : "disabling"} plan`, "error");
    }
  };

  const handleDeletePlan = async (planId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await planService.delete(planId);
      if (result?.success !== false) {
        showAlert(result?.message || "Plan deleted successfully", "success");
        await fetchPlans();
      } else {
        showAlert(result?.message || "Failed to delete plan", "error");
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert(error?.message || "Error deleting plan", "error");
    }
  };

  return (
    <PlanManagementPage
      plans={plans}
      loading={loading}
      selectedCountry={selectedCountry}
      selectedCurrency={
        selectedCountry
          ? String(
              plans.find((plan) => String(plan?.currency || "").trim())?.currency || ""
            ).trim()
          : ""
      }
      onEditPlan={handleEditPlan}
      onTogglePlanStatus={handleTogglePlanStatus}
      onDeletePlan={handleDeletePlan}
      readOnly={readOnly}
    />
  );
};

export default PlanManagement;
